import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  
  CheckCircle,
  Database,
  Filter,
  Check,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import {
  HERO_HIGHLIGHT_STEPS,
  VALIDATION_REASONS,
  VALIDATION_CYCLE,
  VALIDATION_STATS,
} from "../../Data/dataValidationData";

import "../../Style/services/data-validation.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -6, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function DataValidation() {
  const [activeCycleIndex, setActiveCycleIndex] = useState(0);

  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const activeCycle = VALIDATION_CYCLE[activeCycleIndex];
  const ActiveCycleIcon = activeCycle.icon;

  return (
    <div className="validation-page">
      <Seo
        title="Strengthen your Business with a Valid Database | Conectify Tech"
        description="Verify the accuracy and quality of your business contacts before importing. Remove duplicates, maximize open rates, and guarantee high receptiveness."
      />

      {/* Hero Section */}
      <section className="val-hero">
        <Container className="val-hero__container">
          <motion.div
            className="val-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>Enterprise Data Hygiene</span>
            </div>

            <h1 className="val-hero__title">
              Strengthen your Business with a{" "}
              <span className="text-gradient">Valid Database</span>
            </h1>

            <p className="val-hero__lead">
              The method of verifying the accuracy and quality of data before importing and
              processing it is known as Data Validation. This process plays an important role in
              data-driven marketing and helps companies make informed decisions. The data validation
              process ensures the growth of your company in a positive way!
            </p>

            <p className="val-hero__sublead">
              Companies rely on a validated database to ensure high reach with target groups with
              purchase intent. Our validation process removes duplicate records to improve your
              sales and marketing goals. With an accurate and valid B2B email database, we guarantee
              higher open rates and negligible bounce rates to enhance your company's global brand
              presence.
            </p>

            {/* Feature Pills using Filter and CheckCircle */}
            <div className="val-hero__tags">
              <span className="val-tag">
                <Filter size={13} className="text-primary" />
                <span>Auto-Deduplication Filtering</span>
              </span>
              <span className="val-tag">
                <Database size={13} className="text-primary" />
                <span>Zero Syntax Corruptions</span>
              </span>
              <span className="val-tag">
                <CheckCircle size={13} className="text-success" />
                <span>SMTP Verified Inboxes</span>
              </span>
            </div>

            <div className="val-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Validate My Database
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                Request Sample Audit
              </Button>
            </div>
          </motion.div>

          {/* Interactive Hero Validation Dashboard */}
          <motion.div
            className="val-hero__card-wrap"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="val-board-card">
              <div className="val-board-card__header">
                <div className="flex items-center gap-2">
                  <Database size={20} className="text-primary" />
                  <span>Core Validation Continuum</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-primary font-semibold">
                  <Filter size={14} />
                  <span>Dedupe Active</span>
                </div>
              </div>

              <div className="val-stepper-list">
                {HERO_HIGHLIGHT_STEPS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.title}
                      className="val-stepper-row"
                      variants={cardHover}
                      initial="initial"
                      whileHover="hover"
                    >
                      <div className="val-stepper-row__icon">
                        <Icon size={18} />
                      </div>
                      <div className="val-stepper-row__text">
                        <span className="step-tag">Step {item.step}</span>
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Bottom Continuum Footer using Database and CheckCircle */}
              <div className="val-board-card__footer-stats">
                {VALIDATION_STATS.map((stat) => {
                  const StatIcon = stat.icon;
                  return (
                    <div key={stat.label} className="val-stat-pill">
                      <StatIcon size={14} className="text-primary" />
                      <span className="font-bold">{stat.value}</span>
                      <span className="text-muted">{stat.label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="val-board-card__badge">
                <Check size={14} className="text-success" />
                <span>Zero Duplicate Records Guarantee</span>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Why Choose Data Validation Services? */}
      <section className="val-why">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Measurable Impact</span>
            <h2 className="section-title">Why Choose Data Validation Services?</h2>
            <p className="section-desc">
              Data validation not only authenticates your database, it also confirms it while
              ensuring that the data is approachable. Data validation services provide high quality,
              error-free and identical data to enrich your business.
            </p>
          </div>

          <div className="val-why__grid">
            {VALIDATION_REASONS.map((reason) => {
              const Icon = reason.icon;
              return (
                <motion.div
                  key={reason.title}
                  className="val-why-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="val-why-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>{reason.title}</h4>
                  <p>{reason.desc}</p>
                  <div className="val-why-card__check">
                    <CheckCircle size={14} className="text-primary" />
                    <span>Quality Verified</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* The Data Validation Cycle (Interactive Stepper Showcase) */}
      <section className="val-cycle">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Systematic Execution</span>
            <h2 className="section-title">The Data Validation Cycle</h2>
            <p className="section-desc">
              Data validation guarantees a B2B contact list with high quality data to ensure 100%
              receptiveness. Skipping the data validation process can affect your scope and results.
              With manually validated data, you can reach consumers and sell them with purchase
              intentions!
            </p>
          </div>

          {/* Stepper Navigation */}
          <div className="val-cycle__nav">
            {VALIDATION_CYCLE.map((cycle, idx) => (
              <button
                key={cycle.id}
                type="button"
                className={`val-cycle-btn ${activeCycleIndex === idx ? "is-active" : ""}`}
                onClick={() => setActiveCycleIndex(idx)}
              >
                <span className="num">{cycle.step}</span>
                <span className="title">{cycle.title}</span>
              </button>
            ))}
          </div>

          {/* Active Cycle Showcase Box */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCycle.id}
              className="val-cycle-detail"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.3 }}
            >
              <div className="val-cycle-detail__icon-wrap">
                <ActiveCycleIcon size={36} />
                <span className="cycle-badge">Phase {activeCycle.step}</span>
              </div>
              <div className="val-cycle-detail__text">
                <h3>{activeCycle.title}</h3>
                <p>{activeCycle.desc}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </Container>
      </section>

      {/* Bottom CTA */}
      <section className="val-cta">
        <Container>
          <div className="val-cta__box">
            <h2>Ready to Guarantee 100% Receptiveness in Your Pipeline?</h2>
            <p>
              Remove duplicates, eliminate hard bounces, and connect with high-intent decision makers
              using Conectify Tech's multi-channel validation engine.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Initiate Database Validation
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}